import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { VenueListSkeleton, getTranslation } from '@resy/ui-kitchen';
import { InView } from 'react-intersection-observer';
import VenueList from '../../components/VenueList';
import { ALL_DAY } from '../../../../../ui-kitchen/src/constants/resyCore';
import Logger from '../../../../../ui-kitchen/src/utils/logger';

const venueListContainerPropTypes = {
  $location: PropTypes.shape({
    path: PropTypes.func,
    search: PropTypes.func,
  }).isRequired,
  $rootScope: PropTypes.shape({
    $evalAsync: PropTypes.func,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  badge: PropTypes.string,
  collectionSlug: PropTypes.string.isRequired,
  description: PropTypes.string,
  eyebrow: PropTypes.string,
  favoriteVenueIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  fetchVenuesForCollection: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isPlatinum: PropTypes.bool,
  listId: PropTypes.number,
  loadNotifyModal: PropTypes.func.isRequired,
  locationCode: PropTypes.string,
  locationSlug: PropTypes.string,
  name: PropTypes.string,
  selectedDate: PropTypes.string,
  selectedSeats: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedTime: PropTypes.string,
  collections: PropTypes.shape(PropTypes.object),
  isAuthenticated: PropTypes.bool,
  showAuthModal: PropTypes.func.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
};

const VenueListContainer = ({
  $rootScope,
  Analytics: { event },
  badge,
  collections,
  collectionSlug,
  description,
  eyebrow,
  favoriteVenueIds,
  fetchVenuesForCollection,
  isMobile,
  isPlatinum,
  listId,
  loadNotifyModal,
  locationCode,
  locationSlug,
  name,
  selectedDate,
  selectedSeats,
  selectedTime,
  isAuthenticated,
  showAuthModal,
  matchMedia,
}) => {
  const [isInView, setIsInView] = useState(false);

  const key = `${locationCode}-${listId}-${selectedDate}-${selectedTime}-${selectedSeats}-${isAuthenticated}`;
  const venues = collections[key];

  const fetchVenues = () => {
    if (!venues) {
      try {
        const payload = {
          location_id: locationCode,
          collection_id: listId,
          ...(isPlatinum && { collection_slug: collectionSlug }),
          day: selectedDate,
          party_size: selectedSeats,
          time_filter: selectedTime === ALL_DAY ? undefined : moment(selectedTime, 'HHmm').format('HH:mm'),
          limit: 19,
          offset: 1,
          finder: 4,
          isAuth: isAuthenticated,
        };

        fetchVenuesForCollection(payload);
      } catch (err) {
        Logger.error('[VenueListComponent] fetchVenues ERROR:', err);
      }
    }
  };

  const getDescription = () => {
    if (isPlatinum) {
      const termsCopy = getTranslation({ translationKey: 'platinum_terms_link_text', fallback: 'Terms Apply' });
      const ariaText = getTranslation({ translationKey: 'platinum_terms_aria_text', fallback: 'Opens in new window.' });
      const ariaSpan = `<span class="sr-only">${ariaText}</span>`;
      const termsLinkedCopy = `<a href="/platinum#terms" rel="noopener noreferrer" target="_blank">${termsCopy}${ariaSpan}</a>`;

      return `${description} ${termsLinkedCopy}`;
    }
    return description;
  };

  useEffect(() => {
    if (isInView && locationCode && listId && selectedDate && selectedSeats) {
      fetchVenues();
    }
  }, [isInView, locationCode, listId, selectedDate, selectedSeats, selectedTime]);

  const hasNoResults = !venues?.length;

  if (!listId) {
    return null;
  }

  if (venues && venues.length === 0) {
    return null;
  }

  return (
    <InView onChange={(inView) => inView && setIsInView(true)}>
      {({ inView, ref }) => (
        <div className="VenueListContainer" ref={ref}>
          {(!venues) ? (
            <VenueListSkeleton />
          ) : (
            <VenueList
              $rootScope={$rootScope}
              badge={badge}
              collectionSlug={collectionSlug}
              description={getDescription()}
              eyebrow={eyebrow}
              event={event}
              favoriteVenueIds={favoriteVenueIds}
              hasNoResults={hasNoResults}
              inView={inView}
              isMobile={isMobile}
              isPlatinum={isPlatinum}
              listId={listId}
              loadNotifyModal={loadNotifyModal}
              locationCode={locationCode}
              locationSlug={locationSlug}
              name={name}
              restaurants={venues}
              selectedDate={selectedDate}
              selectedSeats={selectedSeats}
              showAuthModal={showAuthModal}
              matchMedia={matchMedia}
              maxCards={isPlatinum ? 4 : null}
            />
          )}
        </div>
      )}
    </InView>
  );
};

VenueListContainer.propTypes = venueListContainerPropTypes;
VenueListContainer.defaultProps = {
  badge: '',
  description: null,
  eyebrow: null,
  isPlatinum: false,
  listId: null,
  locationCode: 'ny',
  locationSlug: 'new-york-ny',
  name: null,
  selectedDate: null,
  selectedSeats: null,
  selectedTime: ALL_DAY,
  collections: undefined,
  isAuthenticated: false,
};

const mapStateToProps = ({ collections }) => ({
  collections: collections.venues.other,
});

VenueListContainer.displayName = 'VenueListContainer';
export { VenueListContainer as ComponentUnderTest, venueListContainerPropTypes };
export default connect(mapStateToProps)(VenueListContainer);
